/* eslint-disable */
import { Box3, Vector3, WebGLRenderer, Scene, PerspectiveCamera, AmbientLight, DirectionalLight } from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader'
// @ts-ignore
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'

import React, { useState, useEffect, useRef } from 'react'
import styles from './index.module.scss'

const ModelViewer = ({
  path,
  width = 500,
  height = 500,
  orbitLimited = false,
  fullScreen = false,
  getSnapshot = { onSnapshot: url => {}, status: false },
  preserveDrawingBuffer = false,
  onLoad = () => {},
}) => {
  // State variables
  const [renderer, setRenderer] = useState(null)
  const [camera, setCamera] = useState(null)
  const [oldPath, setOldPath] = useState('')
  const [modelLoaded, setModelLoaded] = useState(false)

  const divRef = useRef<HTMLDivElement>()

  // Load the GLTF model and set up the scene
  useEffect(() => {
    if (path !== oldPath) {
      setOldPath(path)
      setModelLoaded(false)

      const newRenderer = new WebGLRenderer({ alpha: true, preserveDrawingBuffer })
      newRenderer.setSize(width, height)
      newRenderer.setClearColor(0x000000, 0)
      setRenderer(newRenderer)

      const scene = new Scene()

      const camera = new PerspectiveCamera(75, width / height, 0.1, 1000)
      setCamera(camera)

      const controls = new OrbitControls(camera, newRenderer.domElement)
      if (orbitLimited) {
        controls.enableZoom = false
        controls.minPolarAngle = 0.5
        controls.maxPolarAngle = 2.5
      }

      const render = () => {
        controls.update()
        newRenderer.render(scene, camera)
        requestAnimationFrame(render)
      }
      render()

      const loader = new GLTFLoader()
      const dracoLoader = new DRACOLoader()
      dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/')
      dracoLoader.setDecoderConfig({ type: 'js' })
      loader.setDRACOLoader(dracoLoader)
      async function loadModel(path) {
        const data = await fetch(path).then(r => r.arrayBuffer())
        loader.parse(data, '', gltf => {
          scene.add(gltf.scene)
          setModelLoaded(true)
          onLoad()

          const bbox = new Box3().setFromObject(gltf.scene)
          const center = bbox.getCenter(new Vector3())
          const size = bbox.getSize(new Vector3())

          camera.position.set(0, 0, Math.max(size.x, size.y, size.z) * 1.5)
          controls.target.set(center.x, center.y, center.z)
          controls.update()

          const ambientLight = new AmbientLight(0x404040, 20)
          scene.add(ambientLight)

          const directionalLight = new DirectionalLight(0xffffff, 5)
          scene.add(directionalLight)
        })
      }

      loadModel(path)

      return () => {
        // Dispose of resources
        newRenderer.dispose()
        renderer && renderer.dispose()
      }
    }
  }, [path])

  useEffect(() => {
    if (renderer && !fullScreen) {
      renderer.setSize(width, height)
      camera.aspect = width / height
      camera.updateProjectionMatrix()
    } else if (renderer && fullScreen) {
      renderer.setSize(innerWidth, innerHeight)
      camera.aspect = innerWidth / innerHeight
      camera.updateProjectionMatrix()
    }
  }, [width, height])

  useEffect(() => {
    if (getSnapshot.status) giveSnapshot()
  }, [getSnapshot.status])
  async function giveSnapshot() {
    if (renderer) renderer.domElement.toBlob(blob => getSnapshot.onSnapshot(blob))
  }

  useEffect(() => {
    if (divRef.current) divRef.current.innerHTML = ''
    if (divRef.current && renderer) divRef.current.appendChild(renderer.domElement)
  }, [divRef.current, renderer])

  return (
    <div className="relative">
      <div className={`${styles.skeletonContainer} ${modelLoaded && styles.hide}`}>
        <div className={styles.skeletonLoader} style={{ width, height }} />
      </div>
      <div ref={divRef} />
    </div>
  )
}

export default ModelViewer
